import {
  Component,
  ViewChild,
  OnInit,
  ChangeDetectionStrategy,
  Type,
} from '@angular/core';
import {
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyModule,
} from '@ngx-formly/core';
import { FieldType, FormlyFieldProps } from '@ngx-formly/material/form-field';
import { MatRadioGroup, MatRadioModule } from '@angular/material/radio';
import { ɵobserve as observe } from '@ngx-formly/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { NgStyle } from '@angular/common';

interface RadioProps extends FormlyFieldProps {
  labelPosition?: 'before' | 'after';
  layoutDirection?: 'vertical' | 'horizontal';
}

export interface FormlyRadioFieldConfig extends FormlyFieldConfig<RadioProps> {
  type: 'radio' | Type<RadioTypeComponent>;
}

@Component({
  selector: 'itc-formly-field-mat-radio',
  styleUrls: ['./radio.component.scss'],
  template: `
    <mat-radio-group
      [formlyAttributes]="field"
      [required]="props.required"
      [tabindex]="props.tabindex"
      [formControl]="control"
      [ngStyle]="{
        'flex-direction':
          props.layoutDirection === 'vertical' ? 'column' : 'row'
      }"
      >
      @for (option of optionsArray; track option; let i = $index) {
        <mat-radio-button
          [id]="id + '_' + i"
          [labelPosition]="props.labelPosition"
          [disabled]="props.disabled || option.disabled"
          [value]="option.value"
          color="primary"
          class="form-radio"
          >
          {{ option.label }}
        </mat-radio-button>
      }
    </mat-radio-group>
    `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatRadioModule,
    ReactiveFormsModule,
    MatRadioGroup,
    NgStyle,
    FormlyModule
],
})
export class RadioTypeComponent
  extends FieldType<FieldTypeConfig<RadioProps>>
  implements OnInit
{
  @ViewChild(MatRadioGroup, { static: true }) radioGroup!: MatRadioGroup;
  override defaultOptions = {
    props: {
      hideFieldUnderline: true,
      floatLabel: 'always' as const,
      options: [],
      tabindex: -1,
    },
  };

  control: UntypedFormControl;
  optionsArray: any[];

  ngOnInit() {
    this.control = this?.formControl as UntypedFormControl;

    //assumes we wont be passing an observable for radio options
    this.optionsArray = this.props.options as any[];
  }

  private focusObserver!: ReturnType<typeof observe>;
  ngAfterViewInit() {
    this.focusObserver = observe(this.field, ['focus'], ({ currentValue }) => {
      if (
        this.props.tabindex === -1 &&
        currentValue &&
        this.radioGroup._radios.length > 0
      ) {
        // https://github.com/ngx-formly/ngx-formly/issues/2498
        setTimeout(() => {
          const radio = this.radioGroup.selected
            ? this.radioGroup.selected
            : this.radioGroup._radios.first;
          radio.focus();
        });
      }
    });
  }

  // TODO: find a solution to prevent scroll on focus
  override onContainerClick() {}

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.focusObserver && this.focusObserver.unsubscribe();
  }
}
